import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader, 
  Button, 
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  NoteAdd as NoteAddIcon, 
  BarChart as BarChartIcon, 
  Person as PersonIcon, 
  Speed as SpeedIcon 
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ROUTES, GAME_TYPES, getGameConfig } from '../../config/shared_config';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';

const VALIDATION_CHART_KEYS = [
  { key: 'nouveau', label: 'Nouveau', color: '#bdbdbd' },
  { key: 'en_attente', label: 'En attente', color: '#ed6c02' },
  { key: 'valide', label: 'Validé', color: '#2e7d32' },
  { key: 'inactif', label: 'Inactif', color: '#d32f2f' },
];

export default function AdminDashboardScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalExercises: 0,
    activeExercises: 0,
    exercisesByGame: {},
    exercisesByGameAndValidation: [],
    recentActivity: [],
    totalUsers: 0,
    activeUsers: 0,
    recentActiveUsers: 0,
    recentCombatsPlayed: 0,
    totalGuilds: 0
  });

  useEffect(() => {
    const fetchDashboardStats = async () => {
      setLoading(true);
      try {
        // Utilisation du nouvel endpoint optimisé
        const statsData = await AdminService.getDashboardStats();
        setStats((prev) => ({
          ...prev,
          ...(statsData && typeof statsData === 'object' ? statsData : {}),
          exercisesByGame:
            statsData && typeof statsData === 'object' && statsData.exercisesByGame && typeof statsData.exercisesByGame === 'object'
              ? statsData.exercisesByGame
              : {},
          exercisesByGameAndValidation:
            statsData && Array.isArray(statsData.exercisesByGameAndValidation)
              ? statsData.exercisesByGameAndValidation
              : [],
          recentActivity:
            statsData && typeof statsData === 'object' && Array.isArray(statsData.recentActivity)
              ? statsData.recentActivity
              : [],
        }));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError('Impossible de charger les statistiques du tableau de bord');
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  const StatCard = ({ title, value, icon, color, action }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ 
            backgroundColor: `${color}.light`, 
            borderRadius: '50%', 
            p: 1.5, 
            mr: 2,
            display: 'flex' 
          }}>
            {icon}
          </Box>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        </Box>
        <Typography variant="h3" component="div" sx={{ mb: 2, fontWeight: 'bold' }}>
          {value}
        </Typography>
        {action && (
          <Button 
            variant="outlined" 
            size="small" 
            fullWidth 
            onClick={action.onClick}
            startIcon={action.icon}
          >
            {action.label}
          </Button>
        )}
      </CardContent>
    </Card>
  );

  const ActivityItem = ({ activity }) => {
    const getActivityIcon = (type) => {
      switch (type) {
        case 'exercise_created':
          return <NoteAddIcon fontSize="small" color="success" />;
        case 'exercise_updated':
          return <SpeedIcon fontSize="small" color="primary" />;
        case 'exercise_generated':
          return <BarChartIcon fontSize="small" color="secondary" />;
        default:
          return <NoteAddIcon fontSize="small" />;
      }
    };

    const getActivityText = (type) => {
      switch (type) {
        case 'exercise_created':
          return 'Nouvel exercice créé';
        case 'exercise_updated':
          return 'Exercice mis à jour';
        case 'exercise_generated':
          return 'Exercice généré par LLM';
        default:
          return 'Activité';
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ mr: 2 }}>
          {getActivityIcon(activity.type)}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
            {getActivityText(activity.type)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activity.details}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {new Date(activity.timestamp).toLocaleString('fr-FR', { 
            hour: '2-digit', 
            minute: '2-digit',
            day: '2-digit',
            month: '2-digit'
          })}
        </Typography>
      </Box>
    );
  };

  const formatGameTypeName = (gameType) => {
    const gameConfig = getGameConfig(gameType);
    return gameConfig ? gameConfig.label : gameType;
  };

  const prepareStackedChartData = () => {
    const rows = stats.exercisesByGameAndValidation || [];
    if (rows.length === 0 && stats.exercisesByGame) {
      return Object.entries(stats.exercisesByGame).map(([gameType, count]) => ({
        name: formatGameTypeName(gameType),
        gameType,
        nouveau: count,
        en_attente: 0,
        valide: 0,
        inactif: 0,
        total: count,
      }));
    }

    const byGame = {};
    rows.forEach(({ gameType, validationStatus, isActive, count }) => {
      if (!byGame[gameType]) {
        byGame[gameType] = { gameType, nouveau: 0, en_attente: 0, valide: 0, inactif: 0, total: 0 };
      }
      // Les inactifs sont regroupés en rouge, quel que soit leur statut de validation.
      if (isActive === false) {
        byGame[gameType].inactif += count;
      } else {
        const status = validationStatus || 'nouveau';
        if (status === 'nouveau' || status === 'en_attente' || status === 'valide') {
          byGame[gameType][status] += count;
        }
      }
      byGame[gameType].total += count;
    });

    return Object.values(byGame)
      .map((row) => ({
        ...row,
        name: formatGameTypeName(row.gameType),
      }))
      .sort((a, b) => b.total - a.total);
  };

  const stackedChartData = prepareStackedChartData();
  const chartHeight = Math.max(280, stackedChartData.length * 52);

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Tableau de bord
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Bienvenue dans l'interface d'administration de DictoQuest
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Total des exercices"
            value={stats.totalExercises}
            icon={<NoteAddIcon color="primary" />}
            color="primary"
            action={{ 
              label: "Voir tous", 
              onClick: () => navigate(ROUTES.ADMIN.EXERCISES.LIST),
              icon: <NoteAddIcon fontSize="small" />
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Exercices actifs"
            value={stats.activeExercises}
            icon={<SpeedIcon color="success" />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Utilisateurs"
            value={stats.totalUsers}
            icon={<PersonIcon color="info" />}
            color="info"
            action={{ 
              label: "Gérer", 
              onClick: () => navigate(ROUTES.ADMIN.USERS),
              icon: <PersonIcon fontSize="small" />
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Utilisateurs actifs"
            value={stats.activeUsers}
            icon={<BarChartIcon color="warning" />}
            color="warning"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader 
              title="Exercices par type de jeu et statut de validation" 
              action={
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="small"
                  startIcon={<NoteAddIcon />}
                  onClick={() => navigate(ROUTES.ADMIN.EXERCISES.CREATE)}
                >
                  Créer un exercice
                </Button>
              }
            />
            <CardContent>
              <Box sx={{ height: chartHeight, width: '100%' }}>
                {stackedChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={stackedChartData}
                      margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={130}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value, name) => {
                          const label = VALIDATION_CHART_KEYS.find((s) => s.key === name)?.label || name;
                          return [`${value} exercice(s)`, label];
                        }}
                        labelFormatter={(label) => label}
                      />
                      <Legend
                        formatter={(value) => VALIDATION_CHART_KEYS.find((s) => s.key === value)?.label || value}
                      />
                      {VALIDATION_CHART_KEYS.map(({ key, label, color }) => (
                        <Bar
                          key={key}
                          dataKey={key}
                          name={key}
                          stackId="validation"
                          fill={color}
                          barSize={28}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    Aucun exercice trouvé
                  </Typography>
                )}
              </Box>
              
              {/* Bouton vers les statistiques détaillées */}
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={() => navigate(ROUTES.ADMIN.STATS)}
                  startIcon={<BarChartIcon />}
                >
                  Voir les statistiques détaillées
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Activité récente" />
            <CardContent>
              {stats.recentActivity.length > 0 ? (
                stats.recentActivity.map(activity => (
                  <ActivityItem key={activity.id} activity={activity} />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucune activité récente
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </AdminLayout>
  );
} 